import { useLayoutEffect, useState } from 'react';

export type LayoutDensity = 'comfortable' | 'compact' | 'tight';

export interface ViewportProfile {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isNotebook: boolean;
  isShortHeight: boolean;
  shouldAutoCollapseSidebar: boolean;
  density: LayoutDensity;
}

interface LayoutTokens {
  topbarHeight: number;
  sidebarOpenWidth: number;
  sidebarCollapsedWidth: number;
  shellPadding: number;
  pagePaddingBlock: number;
  pagePaddingInline: number;
  heroPaddingBlock: number;
  heroPaddingInline: number;
  topbarPaddingInline: number;
  topbarGap: number;
  searchMaxWidth: number;
  controlMinHeight: number;
}

const MOBILE_MAX_WIDTH = 800;
const TABLET_MAX_WIDTH = 1024;
const NOTEBOOK_MAX_WIDTH = 1440;
const SHORT_HEIGHT_THRESHOLD = 820;

function roundViewport(value: number, fallback: number) {
  if (!Number.isFinite(value) || value <= 0) {
    return fallback;
  }

  return Math.round(value);
}

export function getViewportProfile(
  viewportWidth: number,
  viewportHeight: number,
): ViewportProfile {
  const width = roundViewport(viewportWidth, NOTEBOOK_MAX_WIDTH);
  const height = roundViewport(viewportHeight, 900);
  const isMobile = width < MOBILE_MAX_WIDTH;
  const isTablet = !isMobile && width < TABLET_MAX_WIDTH;
  const isShortHeight = height < SHORT_HEIGHT_THRESHOLD;
  const isNotebook =
    !isMobile && !isTablet && (width < NOTEBOOK_MAX_WIDTH || isShortHeight);
  const shouldAutoCollapseSidebar = !isMobile && !isTablet && isNotebook;

  const density: LayoutDensity =
    isMobile || isTablet ? 'tight' : isNotebook ? 'compact' : 'comfortable';

  return {
    width,
    height,
    isMobile,
    isTablet,
    isNotebook,
    isShortHeight,
    shouldAutoCollapseSidebar,
    density,
  };
}

function getViewportSnapshot() {
  if (typeof window === 'undefined') {
    return {
      width: NOTEBOOK_MAX_WIDTH,
      height: 900,
    };
  }

  const visualViewport = window.visualViewport;

  return {
    width: roundViewport(visualViewport?.width ?? window.innerWidth, NOTEBOOK_MAX_WIDTH),
    height: roundViewport(
      visualViewport?.height ?? window.innerHeight,
      900,
    ),
  };
}

function getLayoutTokens(profile: ViewportProfile): LayoutTokens {
  if (profile.isMobile || profile.isTablet) {
    return {
      topbarHeight: 56,
      sidebarOpenWidth: 240,
      sidebarCollapsedWidth: 60,
      shellPadding: 12,
      pagePaddingBlock: 14,
      pagePaddingInline: 14,
      heroPaddingBlock: 16,
      heroPaddingInline: 16,
      topbarPaddingInline: 14,
      topbarGap: 12,
      searchMaxWidth: 220,
      controlMinHeight: 36,
    };
  }

  if (profile.density === 'compact') {
    return {
      topbarHeight: 56,
      sidebarOpenWidth: 224,
      sidebarCollapsedWidth: 60,
      shellPadding: 12,
      pagePaddingBlock: 14,
      pagePaddingInline: 16,
      heroPaddingBlock: 18,
      heroPaddingInline: 18,
      topbarPaddingInline: 14,
      topbarGap: 12,
      searchMaxWidth: 320,
      controlMinHeight: 38,
    };
  }

  return {
    topbarHeight: 60,
    sidebarOpenWidth: 240,
    sidebarCollapsedWidth: 64,
    shellPadding: 18,
    pagePaddingBlock: 18,
    pagePaddingInline: 22,
    heroPaddingBlock: 22,
    heroPaddingInline: 24,
    topbarPaddingInline: 18,
    topbarGap: 16,
    searchMaxWidth: 420,
    controlMinHeight: 40,
  };
}

function applyLayoutTokens(profile: ViewportProfile) {
  const root = document.documentElement;
  const tokens = getLayoutTokens(profile);
  const viewportKind = profile.isMobile
    ? 'mobile'
    : profile.isTablet
      ? 'tablet'
      : profile.isNotebook
        ? 'notebook'
        : 'desktop';

  root.dataset['layoutDensity'] = profile.density;
  root.dataset['viewportProfile'] = viewportKind;

  root.style.setProperty('--app-topbar-height', `${tokens.topbarHeight}px`);
  root.style.setProperty(
    '--app-sidebar-open-width',
    `${tokens.sidebarOpenWidth}px`,
  );
  root.style.setProperty(
    '--app-sidebar-collapsed-width',
    `${tokens.sidebarCollapsedWidth}px`,
  );
  root.style.setProperty('--app-shell-padding', `${tokens.shellPadding}px`);
  root.style.setProperty(
    '--app-page-padding-block',
    `${tokens.pagePaddingBlock}px`,
  );
  root.style.setProperty(
    '--app-page-padding-inline',
    `${tokens.pagePaddingInline}px`,
  );
  root.style.setProperty(
    '--app-hero-padding-block',
    `${tokens.heroPaddingBlock}px`,
  );
  root.style.setProperty(
    '--app-hero-padding-inline',
    `${tokens.heroPaddingInline}px`,
  );
  root.style.setProperty(
    '--app-topbar-padding-inline',
    `${tokens.topbarPaddingInline}px`,
  );
  root.style.setProperty('--app-topbar-gap', `${tokens.topbarGap}px`);
  root.style.setProperty(
    '--app-search-max-width',
    `${tokens.searchMaxWidth}px`,
  );
  root.style.setProperty(
    '--app-control-min-height',
    `${tokens.controlMinHeight}px`,
  );
}

export function useViewportScale(): ViewportProfile {
  const [profile, setProfile] = useState(() => {
    const viewport = getViewportSnapshot();
    return getViewportProfile(viewport.width, viewport.height);
  });

  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const update = () => {
      const viewport = getViewportSnapshot();
      const nextProfile = getViewportProfile(viewport.width, viewport.height);
      setProfile(nextProfile);
      applyLayoutTokens(nextProfile);
    };

    update();
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);

    return () => {
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);

  return profile;
}
